import React, { useCallback, useEffect, useMemo, useRef } from "react";
import PropTypes from "prop-types";
import styled from "styled-components";
import ReactDataGrid from "@inovua/reactdatagrid-enterprise";
import "@inovua/reactdatagrid-community/index.css";
import { useTranslation } from "react-i18next";
import { getDataGridI18n } from "../../../utils/getDataGridI18n";
import { getDataGridScrollProps } from "../../../utils/getDataGridScrollProps";
import HoverPopover from "../../../components/HoverPopover";
import HeaderDetails from "../../ClassGradesV2/components/HeaderDetails";
import {
  buildGeneralBoardHighlightLegend,
  resolveCellHighlightColor
} from "../../../utils/generalBoardHighlights";
import {
  aggregateStudentTotal,
  disciplineAbsences,
  visiblePeriodBoards
} from "../../../utils/generalBoardPeriods";
import ColorLegend from "../../../waybee-ui/ColorLegend";

const CellText = styled.p`
  color: rgba(0, 0, 0, 0.5);
  font-size: 18px;
  text-overflow: ellipsis;
  overflow: hidden;
  text-align: ${({ $center }) => ($center ? "center" : "left")};
  width: 100%;
  margin: 0;
`;

const NameLink = styled.span`
  color: inherit;
  cursor: pointer;

  &:hover {
    color: ${({ theme }) => theme.color.secondary.dark};
    text-decoration: underline;
    text-underline-offset: 2px;
  }
`;

const CellWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: ${({ $center }) => ($center ? "center" : "flex-start")};
  width: 100%;
  height: 100%;
  min-height: 48px;
  box-sizing: border-box;
`;

const BoardGrid = styled(ReactDataGrid)`
  .InovuaReactDataGrid__header-group__title {
    width: 100%;
    text-align: center;
  }

  .InovuaReactDataGrid__header-group.board-period-group
    .InovuaReactDataGrid__header-group__title {
    text-align: left;
  }

  .InovuaReactDataGrid__column-header--align-center
    .InovuaReactDataGrid__column-header__content {
    justify-content: center;
    text-align: center;
  }

  .board-call-number.InovuaReactDataGrid__column-header--locked-start
    .InovuaReactDataGrid__column-header__content,
  .board-call-number.InovuaReactDataGrid__cell {
    padding-left: 4px;
    padding-right: 4px;
  }
`;

const PeriodHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  width: 100%;
  min-height: 56px;
  padding: 6px 8px;
  box-sizing: border-box;
  font-size: 16px;
  color: rgba(0, 0, 0, 0.5);
  font-weight: 600;
  text-align: left;
  line-height: 1.3;
`;

const BoardRoot = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 0;
  width: 100%;
  height: 100%;

  /* ColorLegend não deve crescer no modo fullscreen */
  & > *:first-child:not(.InovuaReactDataGrid) {
    flex: 0 0 auto;
  }

  & > .InovuaReactDataGrid,
  & > .class-general-board {
    flex: 1 1 auto;
    min-height: 0;
  }
`;

const TOTAL_GROUP = "total";
const VALUE_COL_WIDTH = 88;
const PERCENT_COL_WIDTH = 84;

function mapLookup(map, key) {
  if (!map || key == null) return null;
  if (Object.prototype.hasOwnProperty.call(map, key)) return map[key];
  const matched = Object.keys(map).find(item => String(item) === String(key));
  return matched != null ? map[matched] : null;
}

function gradeCellValue(gradesByRa, ra, columnId) {
  const row = mapLookup(gradesByRa, ra);
  if (!row) return "";
  const value = row[columnId];
  return value == null || value === "" ? "" : value;
}

function formatBoardGrade(value) {
  if (value == null || value === "") return "";
  const raw = String(value).trim();
  if (!raw || /[a-zA-Z]/.test(raw)) return raw;
  const numeric = Number(raw.replace(",", "."));
  if (!Number.isFinite(numeric)) return raw;
  return numeric.toLocaleString("pt-BR", {
    maximumFractionDigits: 2,
    minimumFractionDigits: 0
  });
}

function formatBoardPercent(value) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return "";
  return `${numeric}%`;
}

function columnsForDiscipline(gradeColumns, idTurmaDisc) {
  return (gradeColumns || []).filter(
    column => String(column.idTurmaDisc) === String(idTurmaDisc)
  );
}

function autosizeGradeColumns(gridApiRef, columnIds) {
  const api = gridApiRef && gridApiRef.current;
  if (
    !api ||
    !columnIds.length ||
    typeof api.setColumnsSizesAuto !== "function"
  ) {
    return;
  }
  api.setColumnsSizesAuto({
    columnIds,
    skipHeader: false
  });
}

const BoardCell = ({ center, children }) => (
  <CellWrap $center={center}>
    <CellText $center={center}>{children}</CellText>
  </CellWrap>
);

BoardCell.propTypes = {
  center: PropTypes.bool,
  children: PropTypes.node
};

BoardCell.defaultProps = {
  center: false,
  children: null
};

function withCellHighlight(column, getCellMeta, resolveColor) {
  return {
    ...column,
    onRender: (cellProps, { data }) => {
      const color = resolveColor(data, getCellMeta(data));
      // eslint-disable-next-line no-param-reassign
      cellProps.style = {
        ...(cellProps.style || {}),
        background: color || undefined
      };
    }
  };
}

const ClassGeneralBoard = ({
  students,
  showCallNumber,
  showRa,
  showAbsencesDiscipline,
  showAbsencesPercentDiscipline,
  showAbsencesTotal,
  showAbsencesPercentTotal,
  showGrades,
  periods,
  visiblePeriodIds,
  boardsByPeriod,
  highlights,
  fillHeight,
  canOpenStudent,
  onStudentClick
}) => {
  const { t } = useTranslation();
  const gridApiRef = useRef(null);

  const dataSource = useMemo(
    () =>
      (students || []).map(student => ({
        callNumber: student.rollCallNum,
        studentId: student.ra,
        name: student.name,
        desistente: student.desistente === "S" ? "S" : "N"
      })),
    [students]
  );

  const periodBoards = useMemo(
    () => visiblePeriodBoards(periods, visiblePeriodIds, boardsByPeriod),
    [boardsByPeriod, periods, visiblePeriodIds]
  );

  const highlightCtxBase = useMemo(
    () => ({
      boardsByPeriod,
      visiblePeriodIds
    }),
    [boardsByPeriod, visiblePeriodIds]
  );

  const cellColor = useCallback(
    (data, cellMeta) =>
      resolveCellHighlightColor(highlights, cellMeta, {
        ...highlightCtxBase,
        student: data
      }),
    [highlightCtxBase, highlights]
  );

  const { columns, groups, gradeColumnIds } = useMemo(() => {
    const cols = [];
    const nextGroups = [];
    const nextGradeColumnIds = [];

    if (showCallNumber) {
      cols.push(
        withCellHighlight(
          {
            name: "callNumber",
            header: t("common.labels.callNumberShort"),
            locked: true,
            editable: false,
            type: "number",
            defaultWidth: 40,
            minWidth: 40,
            maxWidth: 40,
            resizable: false,
            headerAlign: "center",
            textAlign: "center",
            showColumnMenuTool: false,
            headerClassName: "board-call-number",
            className: "board-call-number",
            render: ({ value }) => <BoardCell center>{value}</BoardCell>
          },
          () => ({ kind: "callNumber" }),
          cellColor
        )
      );
    }
    if (showRa) {
      cols.push(
        withCellHighlight(
          {
            name: "studentId",
            header: t("common.labels.ra"),
            locked: true,
            editable: false,
            defaultWidth: 150,
            minWidth: 110,
            showColumnMenuTool: false,
            render: ({ value }) => <BoardCell>{value}</BoardCell>
          },
          () => ({ kind: "ra" }),
          cellColor
        )
      );
    }
    cols.push(
      withCellHighlight(
        {
          name: "name",
          header: t("common.labels.student"),
          locked: true,
          editable: false,
          flex: 1,
          minWidth: 240,
          showColumnMenuTool: false,
          render: ({ value, data }) => (
            <HoverPopover
              key={data.studentId}
              id={data.studentId}
              value={value}
            >
              <BoardCell>
                {canOpenStudent ? (
                  <NameLink
                    role="link"
                    tabIndex={0}
                    onClick={() =>
                      onStudentClick({ ra: data.studentId, name: value })}
                    onKeyDown={event => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        onStudentClick({ ra: data.studentId, name: value });
                      }
                    }}
                  >
                    {value}
                  </NameLink>
                ) : (
                  value
                )}
              </BoardCell>
            </HoverPopover>
          )
        },
        () => ({ kind: "name" }),
        cellColor
      )
    );

    const showDisciplineAbsences =
      showAbsencesDiscipline || showAbsencesPercentDiscipline;
    const showPeriodHeaders = periodBoards.length > 1;

    periodBoards.forEach(({ period, board }) => {
      const periodId = String(period.id);
      const periodGroup = `period-${periodId}`;

      if (showPeriodHeaders) {
        nextGroups.push({
          name: periodGroup,
          header: <PeriodHeader>{period.name}</PeriodHeader>,
          headerAlign: "start",
          headerClassName: "board-period-group"
        });
      }

      (board.disciplines || []).forEach(discipline => {
        const discGrades = showGrades
          ? columnsForDiscipline(board.gradeColumns, discipline.idTurmaDisc)
          : [];
        if (!showDisciplineAbsences && !discGrades.length) return;

        const discGroup = `disc-${periodId}-${discipline.idTurmaDisc}`;
        nextGroups.push({
          name: discGroup,
          header: <HeaderDetails title={discipline.name} compact />,
          headerAlign: "center",
          ...(showPeriodHeaders ? { group: periodGroup } : {})
        });

        if (showAbsencesDiscipline) {
          cols.push(
            withCellHighlight(
              {
                name: `absences-${periodId}-${discipline.idTurmaDisc}`,
                header: t("classes.details.boardAbsences"),
                group: discGroup,
                editable: false,
                defaultWidth: PERCENT_COL_WIDTH,
                minWidth: 64,
                headerAlign: "center",
                textAlign: "center",
                showColumnMenuTool: false,
                render: ({ data }) => (
                  <BoardCell center>
                    {
                      disciplineAbsences(
                        board,
                        data.studentId,
                        discipline.idTurmaDisc
                      ).absences
                    }
                  </BoardCell>
                )
              },
              () => ({
                kind: "absencesDiscipline",
                periodId,
                idTurmaDisc: discipline.idTurmaDisc
              }),
              cellColor
            )
          );
        }

        if (showAbsencesPercentDiscipline) {
          cols.push(
            withCellHighlight(
              {
                name: `percent-${periodId}-${discipline.idTurmaDisc}`,
                header: t("classes.details.boardAbsencesPercent"),
                group: discGroup,
                editable: false,
                defaultWidth: PERCENT_COL_WIDTH,
                minWidth: 72,
                headerAlign: "center",
                textAlign: "center",
                showColumnMenuTool: false,
                render: ({ data }) => (
                  <BoardCell center>
                    {formatBoardPercent(
                      disciplineAbsences(
                        board,
                        data.studentId,
                        discipline.idTurmaDisc
                      ).percent
                    )}
                  </BoardCell>
                )
              },
              () => ({
                kind: "absencesPercentDiscipline",
                periodId,
                idTurmaDisc: discipline.idTurmaDisc
              }),
              cellColor
            )
          );
        }

        discGrades.forEach(column => {
          const columnName = `${periodId}-${column.id}`;
          nextGradeColumnIds.push(columnName);
          const isStage = column.kind === "etapa";
          const isAverage = !isStage && String(column.CODPROVA) === "MED";
          cols.push(
            withCellHighlight(
              {
                name: columnName,
                header: isAverage
                  ? t("classes.details.boardAverage")
                  : column.header,
                group: discGroup,
                editable: false,
                defaultWidth: VALUE_COL_WIDTH,
                minWidth: 72,
                headerAlign: "center",
                textAlign: "center",
                showColumnMenuTool: false,
                render: ({ data }) => (
                  <BoardCell center>
                    {formatBoardGrade(
                      gradeCellValue(
                        board.gradesByRa,
                        data.studentId,
                        column.id
                      )
                    )}
                  </BoardCell>
                )
              },
              () => ({
                kind: "grade",
                periodId,
                idTurmaDisc: column.idTurmaDisc,
                gradeColumnId: column.id,
                isAverage
              }),
              cellColor
            )
          );
        });
      });
    });

    const showTotalGroup = showAbsencesTotal || showAbsencesPercentTotal;
    if (showTotalGroup) {
      nextGroups.push({
        name: TOTAL_GROUP,
        header: (
          <HeaderDetails title={t("classes.details.boardTotal")} compact />
        ),
        headerAlign: "center"
      });

      if (showAbsencesTotal) {
        cols.push(
          withCellHighlight(
            {
              name: "absences-total",
              header: t("classes.details.boardAbsences"),
              group: TOTAL_GROUP,
              editable: false,
              defaultWidth: PERCENT_COL_WIDTH,
              minWidth: 64,
              headerAlign: "center",
              textAlign: "center",
              showColumnMenuTool: false,
              render: ({ data }) => (
                <BoardCell center>
                  {aggregateStudentTotal(periodBoards, data.studentId).absences}
                </BoardCell>
              )
            },
            () => ({ kind: "absencesTotal" }),
            cellColor
          )
        );
      }

      if (showAbsencesPercentTotal) {
        cols.push(
          withCellHighlight(
            {
              name: "percent-total",
              header: t("classes.details.boardAbsencesPercent"),
              group: TOTAL_GROUP,
              editable: false,
              defaultWidth: PERCENT_COL_WIDTH,
              minWidth: 72,
              headerAlign: "center",
              textAlign: "center",
              showColumnMenuTool: false,
              render: ({ data }) => (
                <BoardCell center>
                  {formatBoardPercent(
                    aggregateStudentTotal(periodBoards, data.studentId).percent
                  )}
                </BoardCell>
              )
            },
            () => ({ kind: "absencesPercentTotal" }),
            cellColor
          )
        );
      }
    }

    return {
      columns: cols,
      groups: nextGroups,
      gradeColumnIds: nextGradeColumnIds
    };
  }, [
    canOpenStudent,
    cellColor,
    onStudentClick,
    periodBoards,
    showAbsencesDiscipline,
    showAbsencesPercentDiscipline,
    showAbsencesPercentTotal,
    showAbsencesTotal,
    showCallNumber,
    showGrades,
    showRa,
    t
  ]);

  const gridHeight = Math.min(550, Math.max(220, 52 + dataSource.length * 50));

  const handleGridReady = useCallback(
    api => {
      gridApiRef.current = api;
      requestAnimationFrame(() => {
        autosizeGradeColumns(gridApiRef, gradeColumnIds);
      });
    },
    [gradeColumnIds]
  );

  useEffect(() => {
    const frame = requestAnimationFrame(() => {
      autosizeGradeColumns(gridApiRef, gradeColumnIds);
    });
    return () => cancelAnimationFrame(frame);
  }, [dataSource, gradeColumnIds, showGrades]);

  const colorLegendItems = useMemo(() => {
    const formatKindLabel = (kind, examName) => {
      if (kind === "gradeAny" && examName) {
        return t("settings.generalBoard.highlights.kinds.gradeAnyNamed", {
          name: examName
        });
      }
      if (kind === "gradeByName" && examName) {
        return t("settings.generalBoard.highlights.kinds.gradeAnyNamed", {
          name: examName
        });
      }
      return t(`settings.generalBoard.highlights.kinds.${kind}`, kind);
    };

    return buildGeneralBoardHighlightLegend(
      highlights,
      dataSource,
      highlightCtxBase
    ).map(item => {
      const baseLabel = formatKindLabel(
        item.kind,
        item.kind === "gradeByName" || item.kind === "gradeAny"
          ? item.value
          : null
      );
      if (item.kind === "desistente") {
        return { id: item.id, color: item.color, label: baseLabel };
      }
      const operatorLabel = t(
        `settings.generalBoard.highlights.operators.${item.operator}`,
        item.operator
      );
      let label = `${baseLabel} · ${operatorLabel}`;
      if (item.operator === "between" && (item.value || item.valueTo)) {
        label = `${label} ${item.value || ""}–${item.valueTo || ""}`.trim();
      } else if (
        item.operator !== "empty" &&
        item.operator !== "notEmpty" &&
        item.value != null &&
        item.value !== "" &&
        item.kind !== "gradeByName"
      ) {
        label = `${label} ${item.value}`;
      }
      return { id: item.id, color: item.color, label };
    });
  }, [dataSource, highlightCtxBase, highlights, t]);

  return (
    <BoardRoot>
      <ColorLegend title={t("common.colorLegend")} items={colorLegendItems} />
      <BoardGrid
        licenseKey={process.env.REACT_APP_DATA_GRID_LICENSE_KEY}
        className="class-general-board"
        idProperty="studentId"
        columns={columns}
        groups={groups}
        dataSource={dataSource}
        rowHeight={50}
        style={
          fillHeight
            ? { height: "100%", minHeight: 0 }
            : { minHeight: gridHeight }
        }
        i18n={getDataGridI18n(t)}
        scrollProps={getDataGridScrollProps()}
        reorderColumns={false}
        resizable
        sortable={false}
        showColumnMenuToolOnHover={false}
        enableColumnAutosize
        skipHeaderOnAutoSize={false}
        showZebraRows={false}
        enableKeyboardNavigation={false}
        onReady={handleGridReady}
      />
    </BoardRoot>
  );
};

ClassGeneralBoard.propTypes = {
  students: PropTypes.arrayOf(
    PropTypes.shape({
      rollCallNum: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      ra: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      name: PropTypes.string,
      desistente: PropTypes.oneOf(["S", "N"])
    })
  ),
  showCallNumber: PropTypes.bool,
  showRa: PropTypes.bool,
  showAbsencesDiscipline: PropTypes.bool,
  showAbsencesPercentDiscipline: PropTypes.bool,
  showAbsencesTotal: PropTypes.bool,
  showAbsencesPercentTotal: PropTypes.bool,
  showGrades: PropTypes.bool,
  periods: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      name: PropTypes.string
    })
  ),
  visiblePeriodIds: PropTypes.arrayOf(
    PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  ),
  boardsByPeriod: PropTypes.object,
  highlights: PropTypes.arrayOf(PropTypes.object),
  fillHeight: PropTypes.bool,
  canOpenStudent: PropTypes.bool,
  onStudentClick: PropTypes.func
};

ClassGeneralBoard.defaultProps = {
  students: [],
  showCallNumber: true,
  showRa: true,
  showAbsencesDiscipline: true,
  showAbsencesPercentDiscipline: true,
  showAbsencesTotal: true,
  showAbsencesPercentTotal: true,
  showGrades: false,
  periods: [],
  visiblePeriodIds: [],
  boardsByPeriod: {},
  highlights: [],
  fillHeight: false,
  canOpenStudent: false,
  onStudentClick: () => {}
};

export default ClassGeneralBoard;
